import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { 
  Target,
  Clock,
  ChevronRight,
  AlertTriangle,
  TrendingUp,
  Briefcase,
  Users,
  MessageSquare,
  Play,
  Loader2,
  CheckCircle2,
  RefreshCw
} from 'lucide-react';

const phaseIcons = {
  phase_5: Briefcase,
  phase_6: Users,
  phase_7: MessageSquare
};

const phaseLabels = {
  phase_5: 'Phase 5: Job Mastery',
  phase_6: 'Phase 6: Sourcing Labs',
  phase_7: 'Phase 7: Role Play'
};

const RetakeGuidance = ({ onRetakeComplete }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [guidance, setGuidance] = useState(null);
  const [expandedPhase, setExpandedPhase] = useState(null);

  useEffect(() => {
    fetchGuidance();
  }, []);

  const fetchGuidance = async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/certification/retake-guidance');
      setGuidance(res.data);
      
      // Auto-expand first phase with items to retake
      for (const phase of ['phase_5', 'phase_6', 'phase_7']) {
        if (res.data[phase]?.low_scoring_items?.length > 0) {
          setExpandedPhase(phase);
          break;
        }
      }
    } catch (err) {
      console.error('Failed to fetch retake guidance:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetakeItem = (item) => {
    // Navigate to the specific item's retake route
    navigate(item.route);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!guidance || guidance.total_items_to_retake === 0) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20">
        <CardContent className="py-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-200">
            All Items Meet the Threshold!
          </h3>
          <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2">
            All your completed items scored 70% or higher. Great job!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="retake-guidance-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-amber-500" />
          Targeted Improvement Plan
        </CardTitle>
        <CardDescription>
          Focus on these specific items that scored below 70%. You don&apos;t need to redo entire phases!
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{guidance.total_items_to_retake}</p>
              <p className="text-sm text-muted-foreground">Items to improve</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">~{guidance.estimated_time_minutes} min</p>
              <p className="text-sm text-muted-foreground">Estimated time</p>
            </div>
          </div>
        </div>

        {/* Phase Sections */}
        {['phase_5', 'phase_6', 'phase_7'].map((phaseKey) => {
          const phaseData = guidance[phaseKey];
          const PhaseIcon = phaseIcons[phaseKey];
          const hasItems = phaseData?.low_scoring_items?.length > 0;
          const isExpanded = expandedPhase === phaseKey;
          
          if (!hasItems) return null;
          
          return (
            <div key={phaseKey} className="border rounded-lg overflow-hidden">
              {/* Phase Header */}
              <button
                className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedPhase(isExpanded ? null : phaseKey)}
                data-testid={`expand-${phaseKey}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    phaseData.needs_improvement 
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/30' 
                      : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30'
                  }`}>
                    <PhaseIcon className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">{phaseLabels[phaseKey]}</p>
                    <p className="text-sm text-muted-foreground">
                      {phaseData.low_scoring_items.length} item(s) below 70%
                      {phaseData.phase_avg && ` • Avg: ${phaseData.phase_avg}%`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={phaseData.needs_improvement ? "destructive" : "outline"}>
                    {phaseData.low_scoring_items.length} to retake
                  </Badge>
                  <ChevronRight className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </div>
              </button>

              {/* Expanded Items */}
              {isExpanded && (
                <div className="border-t bg-muted/10 p-4 space-y-3">
                  {phaseData.low_scoring_items.map((item, idx) => (
                    <div 
                      key={item.id || idx}
                      className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border"
                      data-testid={`retake-item-${item.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{item.title}</p>
                          {item.category && (
                            <Badge variant="outline" className="text-xs">
                              {item.category}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{item.description || item.tip}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-red-500 font-medium">
                            Current: {item.current_score}%
                          </span>
                          <span className="text-muted-foreground">→</span>
                          <span className="text-emerald-600 font-medium">
                            Need: {item.needed_score}%
                          </span>
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            ~{item.estimated_time} min
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleRetakeItem(item)}
                        className="ml-4"
                        data-testid={`retake-btn-${item.id}`}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Retake
                      </Button>
                    </div>
                  ))}
                  
                  {/* Quick tip */}
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg flex items-start gap-2">
                    <TrendingUp className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <strong>Tip:</strong> {phaseData.low_scoring_items[0]?.tip}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Refresh Button */}
        <div className="text-center pt-4">
          <Button
            variant="outline"
            onClick={() => {
              fetchGuidance();
              if (onRetakeComplete) onRetakeComplete();
            }}
            data-testid="refresh-retake-guidance"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Progress
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RetakeGuidance;
